import React, { Fragment, useMemo, useCallback } from 'react';
import filter from 'lodash/filter';

import {
  IsFetched,
  ErrorMessage,
  FetchItemsLoadMoreItems
} from '../../../../../types';

import {
  ProductNanoID,
  FetchProductsLimit,
  FetchProductsPage,
  UpdateProductCacheAction,
  OnProductSimilarSearchAction,
  ChangeProductsFiltersFunc,
  FetchProductsFilters
} from '../../../../products/productsTypes';
import {
  ThreeDStockProductsCompactListProduct,
  ThreeDStockProductsCompactListProducts
} from './ThreeDStockProductsCompactList.types';

import { ThreeDStockProductsCompactListItem } from '../ThreeDStockProductsCompactListItem';
import { ThreeDStockProductsListLightboxTitle } from '../ThreeDStockProductsList/components/ThreeDStockProductsListLightboxTitle';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { Loading } from '../../../../../helpers/Loading';
import { NoResults } from '../../../../../helpers/NoResults';
import {
  LightboxWrapper,
  useLightboxWrapper,
  LightboxRenderImageTitle,
  LightboxRenderCustomButtons
} from '../../../../../helpers/LightboxWrapper';
import { LoadMoreButtonHelper } from '../../../../../helpers/buttons/LoadMoreButtonHelper';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';

import { Files } from '../../../../../utils/Files';

import { ProductsPermissions } from '../../../../products/productsConstants';
import { ThreeDStockProductsListLightboxBottomButtons } from '../ThreeDStockProductsList/components/ThreeDStockProductsListLightboxBottomButtons';

type ThreeDStockProductsCompactListProps = {
  onProductEditButtonMouseEnter?: (productNanoId: ProductNanoID) => void;
  products: ThreeDStockProductsCompactListProducts;
  productsError: ErrorMessage;
  productsFetched: IsFetched;
  productsIsPlaceholderData: boolean;
  productsTotalCount: number;
  hasNextProductsPage: boolean;
  productsPage: FetchProductsPage;
  productsLimit: FetchProductsLimit;
  loadMoreProducts: FetchItemsLoadMoreItems;
  productsFetchingNextPage: boolean;
  changeProductsFilters: ChangeProductsFiltersFunc;
  productsFilters: FetchProductsFilters;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsCompactListProduct>;
  onProductSimilarSearchFilter?: OnProductSimilarSearchAction;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  findSimilarButtonAction: string;
  editButtonAction: string;
};

function ThreeDStockProductsCompactList({
  onProductEditButtonMouseEnter,
  products,
  productsError,
  productsFetched,
  productsIsPlaceholderData,
  productsTotalCount,
  hasNextProductsPage,
  productsPage,
  productsLimit,
  loadMoreProducts,
  productsFetchingNextPage,
  onProductSimilarSearchFilter,
  updateProductCache,
  changeProductsFilters,
  productsFilters,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  findSimilarButtonAction,
  editButtonAction
}: ThreeDStockProductsCompactListProps) {
  const productsInLightbox =
    useMemo<ThreeDStockProductsCompactListProducts>(() => {
      return filter<ThreeDStockProductsCompactListProduct>(
        products,
        (product) => product.image?.file && Files.isImage(product.image.file)
      );
    }, [products]);

  const {
    handleLightboxClose,
    handleLightboxNext,
    handleLightboxOpen,
    handleLightboxOpenOnSlide,
    handleLightboxPrev,
    index,
    imagesCount,
    imageItem,
    lightBoxOpened,
    mainSrc,
    prevSrc,
    nextSrc
  } = useLightboxWrapper({
    items: productsInLightbox
  });

  const renderImageTitle = useCallback<LightboxRenderImageTitle>(
    ({ index }) => {
      const product = productsInLightbox[index];
      if (!product) {
        return null;
      }

      return <ThreeDStockProductsListLightboxTitle product={product} />;
    },
    [productsInLightbox]
  );

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={productsError} />
      <LoadingSkeleton loaded={productsIsPlaceholderData || productsFetched}>
        {productsTotalCount === 0 && <NoResults addErrorClassName="m-4" />}
        {productsTotalCount > 0 && (
          <div className="min-w-0 -mx-2 space-y-1">
            <div className="flex justify-center items-center">
              <Loading loaded={!productsIsPlaceholderData} />
            </div>

            {products.map((product) => (
              <ThreeDStockProductsCompactListItem
                key={product.uuid}
                product={product}
                onProductEditButtonMouseEnter={onProductEditButtonMouseEnter}
                onLightboxOpen={handleLightboxOpenOnSlide}
                onProductSimilarSearchFilter={onProductSimilarSearchFilter}
                updateProductCache={updateProductCache}
                changeProductsFilters={changeProductsFilters}
                productsFilters={productsFilters}
                downloadButtonAction={downloadButtonAction}
                downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
                findSimilarButtonAction={findSimilarButtonAction}
                editButtonAction={editButtonAction}
              />
            ))}

            {hasNextProductsPage && (
              <LoadMoreButtonHelper
                action={
                  ProductsPermissions.READ_PRODUCTS_LOAD_MORE_BUTTON_ITEMS_COUNT
                }
                dataGa="products-load-more-button"
                itemsTotalCount={productsTotalCount}
                page={productsPage}
                limit={productsLimit}
                loadMoreItems={loadMoreProducts}
                itemsFetchingNextPage={productsFetchingNextPage}
              />
            )}
          </div>
        )}
      </LoadingSkeleton>
      <LightboxWrapper
        handleLightboxClose={handleLightboxClose}
        handleLightboxNext={handleLightboxNext}
        handleLightboxOpen={handleLightboxOpen}
        handleLightboxPrev={handleLightboxPrev}
        index={index}
        imagesCount={imagesCount}
        imageItem={imageItem}
        lightBoxOpened={lightBoxOpened}
        mainSrc={mainSrc}
        nextSrc={nextSrc}
        prevSrc={prevSrc}
        renderImageTitle={renderImageTitle}
        // renderCustomButtons={renderLightboxButtons}
        withFullScreenButton
      />
    </Fragment>
  );
}

export default ThreeDStockProductsCompactList;
